import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get authorization code from URL
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');

        if (error) {
          setStatus('error');
          setError('Google login was cancelled or failed');
          const redirectPath = sessionStorage.getItem('oauth_redirect_path') || '/';
          sessionStorage.removeItem('oauth_redirect_path');
          sessionStorage.removeItem('oauth_in_progress');
          setTimeout(() => navigate(redirectPath), 3000);
          return;
        }

        if (!code) {
          setStatus('error');
          setError('No authorization code received');
          const redirectPath = sessionStorage.getItem('oauth_redirect_path') || '/';
          sessionStorage.removeItem('oauth_redirect_path');
          sessionStorage.removeItem('oauth_in_progress');
          setTimeout(() => navigate(redirectPath), 3000);
          return;
        }

        setStatus('processing');

        // Send code to backend
        const response = await api.post('/api/auth/oauth/google/callback', {
          code: code,
          redirectUri: window.location.origin + '/auth/callback'
        });

        if (response.data.token) {
          // Save token to localStorage
          localStorage.setItem('token', response.data.token);
          
          // Save user data if available
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }

          setStatus('success');
          
          // Get the saved redirect path, or default to home
          const redirectPath = sessionStorage.getItem('oauth_redirect_path') || '/';
          sessionStorage.removeItem('oauth_redirect_path');
          sessionStorage.removeItem('oauth_in_progress');
          
          // Redirect back to the original page or home
          setTimeout(() => {
            navigate(redirectPath);
            window.location.reload(); // Refresh to update auth state
          }, 1000);
        } else {
          throw new Error('No token received');
        }

      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        
        // Get detailed error message
        const errorMessage = err.response?.data?.error || 
                           err.response?.data?.message || 
                           err.message || 
                           'Authentication failed';
        
        // Check if it's a configuration issue
        if (errorMessage.includes('not configured') || errorMessage.includes('Google OAuth')) {
          setError('Google Login is not set up yet. Please use Email/Password login or contact administrator.');
        } else {
          setError(errorMessage);
        }
        
        const redirectPath = sessionStorage.getItem('oauth_redirect_path') || '/';
        sessionStorage.removeItem('oauth_redirect_path');
        sessionStorage.removeItem('oauth_in_progress');
        setTimeout(() => navigate(redirectPath), 5000);
      }
    };

    handleCallback();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
        {status === 'processing' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Login</h2>
            <p className="text-gray-600">Please wait while we authenticate your account...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Login Successful!</h2>
            <p className="text-gray-600">Redirecting you to the platform...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Authentication Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Redirecting to home page...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;

